// Libs
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
import { InfoPackageForm, SelectProductPackageForm, SetProductPackageForm } from '~/components/module/package';
// Others
import { defaultValuesPackageForm, schema } from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBodyUpdate } from '~/utils/interface/common';
import { IFormPackage } from '~/utils/interface/package';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import { createPackage, getDetailPackage, updatePackage } from '~/thunks/package/packageThunk';
import { compareDataUpdate, createFormData, isEmptyObject, removeEmptyObjects } from '~/utils/helper/common';
// Styles, images, icons
import styles from './PackageForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const PackageForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/packages', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { id: packageId } = useParams();
  const isClone = location.pathname.endsWith('/clone');

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    setError,
    formState: { errors, isDirty, dirtyFields, isSubmitted },
  } = useForm<IFormPackage>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesPackageForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormPackage>(defaultValuesPackageForm);

  const [deleteAvatar, setDeleteAvatar] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const disable = useMemo(() => {
    return packageId && !isClone ? Object.keys(dirtyFields).length === 0 : false;
  }, [packageId, JSON.stringify(dirtyFields)]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!packageId) return;

    handleGetDetailPackage(packageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailPackage = (packageId: string) => {
    setIsLoading(true);

    dispatch(getDetailPackage(packageId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, packageImage, category, ...remainingData } = res?.data;

        const body: IFormPackage = {
          ...remainingData,
          title: remainingData.title ?? EMPTY_STRING,
          rentalPrice: remainingData.rentalPrice ?? EMPTY_STRING,
          isTaxable: remainingData.isTaxable ?? false,
          description: remainingData.description ?? EMPTY_STRING,
          note: remainingData.note ?? EMPTY_STRING,
          isShowItem: remainingData?.isShowItem ?? false,
          packageImage: packageImage && !isClone ? { url: packageImage } : {},
          packageItems:
            remainingData.packageItems.map((item) => ({ ...item, variantId: item.variantId || undefined })) ?? [],
          ...(category ? { categoryId: category.id } : { categoryId: EMPTY_STRING }),
          ...(category ? { categoryName: category.name } : { categoryName: EMPTY_STRING }),
        };

        setInitialData(body);
        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormPackage = (data: IFormPackage) => {
    if (packageId && !isClone) {
      handleEditPackage(data);
    } else {
      handleAddPackage(data);
    }

    setIsLoading(true);
  };

  const handleAddPackage = async (data: IFormPackage) => {
    const {
      productItems,
      selectIdProductInventory,
      selectIdVariantInventory,
      selectIdProductSetProduct,
      selectIdVariantSetProduct,
      selectQuantitySetProduct,
      deleteProduct,
      deleteItemIds,
      isDeleteImage,
      categoryName,
      ...restData
    } = data;

    const convertData: IFormPackage = {
      ...restData,
      packageItems: data.packageItems?.map((item) => {
        const { name, subtotal, inventoryImages, variantName, type, id, ...rest } = item;
        return rest;
      }),
    };

    if (isClone && data?.packageImage?.url) {
      delete convertData.packageImage;
    }

    const formData = await createFormData(removeEmptyObjects(convertData), ['packageImage']);

    dispatch(createPackage(formData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditPackage = async (data: IFormPackage) => {
    const {
      productItems,
      selectIdProductInventory,
      selectIdVariantInventory,
      selectIdProductSetProduct,
      selectIdVariantSetProduct,
      selectQuantitySetProduct,
      deleteProduct,
      categoryName,
      ...restData
    } = data;

    const convertData: IFormPackage = {
      ...restData,
      packageItems: data.packageItems?.map((item) => {
        const {
          name,
          subtotal,
          inventoryImages,
          laborCost,
          isRequired,
          isShowPrice,
          variantName,
          variantId,
          type,
          ...rest
        } = item;
        return {
          ...rest,
          isRequired: Boolean(isRequired),
          isShowPrice: Boolean(isShowPrice),
          variantId: variantId || undefined,
          id:
            initialData.packageItems.find((itemRemember) => itemRemember.variantId === item.variantId)?.id || undefined,
        };
      }),
      packageImage: data.packageImage,
      isDeleteImage: isEmptyObject(data?.packageImage) ? deleteAvatar : false,
    };

    const dataChange = compareDataUpdate(initialData, convertData) as IFormPackage;

    const formData = await createFormData(dataChange, ['packageImage']);

    const params: IBodyUpdate<FormData> = {
      id: packageId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updatePackage(params))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleGetTitle = () => {
    if (packageId && !isClone) {
      return t('package_form_title_edit');
    }

    if (packageId && isClone) {
      return t('package_form_title_clone');
    }

    return t('package_form_title_add');
  };
  //#endregion Handle Function

  return (
    <Box id='packageFormPage' className={cx('container')}>
      <BaseToolbar title={handleGetTitle()} isBack>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' onClick={handleSubmit(handleFormPackage)} disabled={disable}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </BaseToolbar>

      <FormProvider
        {...({
          control,
          watch,
          reset,
          trigger,
          setValue,
          getValues,
          setError,
          formState: { errors, isDirty, dirtyFields, isSubmitted },
        } as UseFormReturn<IFormPackage>)}
      >
        <Stack gap={4}>
          <InfoPackageForm onDeleteImage={(value) => setDeleteAvatar(value)} />

          <SetProductPackageForm detailPackage={initialData.packageItems} />

          <SelectProductPackageForm detailPackage={initialData.packageItems} />
        </Stack>
      </FormProvider>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default PackageForm;
